<script setup lang="ts">
import type { Ref } from 'vue';
import type { TableFoldProps as DispatchDriversTableFoldProps } from '@/components/tableFolds/DispatchDriversTableFold.vue';
import type { HeadersData } from '@/composables/useTable';

import { computed, ref } from 'vue';

// import KBtn from '@/components/btns/KBtn.vue';
import TableFoldBtn from '@/components/btns/TableFoldBtn.vue';
import TableLoader from '@/components/loaders/TableLoader.vue';
import SortThTableCell from '@/components/tableCells/SortThTableCell.vue';
import DispatchDriversTableFold from '@/components/tableFolds/DispatchDriversTableFold.vue';
import TableEmptyText from '@/components/texts/TableEmptyText.vue';
import useTable from '@/composables/useTable';

import tableStyles from '@/styles/table.module.scss';

export interface DataItem {
  id: number;
  status: string;
  driver: number;
  name: string;
  jobCount: number;
  type: string;
  notes: string;
  fold: DispatchDriversTableFoldProps;
}

export interface Props {
  loading?: boolean;
  data?: DataItem[];
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  data: () => [],
});

const openFoldKey = ref(NaN);

const useTableValues = useTable(props);
const { actives, ascs, handleSort } = useTableValues;
const sortedData = useTableValues.sortedData as Ref<DataItem[]>;

const headersData = computed<HeadersData>(function () {
  // if (props.isStaff) { // use computed to keep the pattern from other tables
  return [
    {
      value: 'driver',
      title: '#',
      colspan: 2,
    },
    {
      value: 'name',
      title: 'Name',
    },
    {
      value: 'jobCount',
      title: 'Jobs',
    },
    {
      value: 'type',
      title: 'Type',
    },
    // TODO don't have any actions or notes yet
    // {
    //   value: 'notes',
    //   title: 'Notes',
    // },
  ];
});

const foldColspan = computed(() => {
  return headersData.value.length + 1;
});

function handleClick(dataItemId: number): void {
  if (openFoldKey.value === dataItemId) {
    openFoldKey.value = NaN;
  } else {
    openFoldKey.value = dataItemId;
  }
}
</script>

<template>
  <table
    :class="[
      tableStyles.table,
      tableStyles.folded,
      tableStyles.hovered,
      tableStyles.bordered,
      $style.dispatchDriversTable,
    ]"
    data-testid="dispatchDriversTable"
  >
    <thead>
      <tr>
        <template
          v-for="(headerItem, headerIndex) in headersData"
          :key="headerIndex"
        >
          <SortThTableCell
            :colspan="headerItem.colspan"
            :actives="actives"
            :ascs="ascs"
            :handle-sort="handleSort"
            :header-item-value="headerItem.value"
            :is-not-sortable="headerItem.isNotSortable"
            :class="$style[headerItem.value + 'Th']"
          >
            {{ headerItem.title }}
          </SortThTableCell>
        </template>
        <!-- <th :class="$style.actionsTh"></th> -->
      </tr>
    </thead>
    <tr v-if="props.loading">
      <td :colspan="foldColspan" class="!h-64 p-0">
        <TableLoader />
      </td>
    </tr>
    <tr v-else-if="!sortedData?.length">
      <td :colspan="foldColspan" class="border-b-0 p-0">
        <TableEmptyText />
      </td>
    </tr>
    <tbody v-else>
      <template v-for="dataItem in sortedData" :key="dataItem.id">
        <tr
          :class="[
            tableStyles.foldTitle,
            { [tableStyles.foldOpen]: openFoldKey === dataItem.id },
          ]"
        >
          <td :class="$style.foldTd">
            <TableFoldBtn
              :class="tableStyles.foldBtn"
              :is-open="openFoldKey === dataItem.id"
              :disabled="dataItem.jobCount === 0"
              @click="handleClick(dataItem.id)"
            />
          </td>
          <td :class="$style.driverTd">
            <v-badge
              :class="$style.badge"
              dot
              inline
              :color="dataItem.status"
            ></v-badge>
            {{ dataItem.driver }}
          </td>
          <td :class="$style.nameTd">
            {{ dataItem.name }}
          </td>
          <td :class="$style.jobCountTd">
            {{ dataItem.jobCount }}
          </td>
          <td :class="$style.typeTd">
            {{ dataItem.type }}
          </td>
          <!-- TODO don't have any actions or notes yet
          <td :class="$style.notesTd">
            {{ dataItem.notes }}
          </td>
          <td :class="$style.actionsTd">
            <menu :class="$style.actionsMenuList">
              <li :class="$style.actionsMenuListItem">
                <KBtn size="xs" variant="text" icon="mdi-dots-vertical"></KBtn>
              </li>
            </menu>
          </td>
          -->
        </tr>
        <tr
          :class="[
            tableStyles.foldContent,
            { [tableStyles.foldOpen]: openFoldKey === dataItem.id },
          ]"
        >
          <td :colspan="foldColspan">
            <div v-if="openFoldKey === dataItem.id">
              <DispatchDriversTableFold v-bind="dataItem.fold" />
            </div>
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';
@use '@/styles/table.module.scss' as tableStyles;

.dispatchDriversTable {
  width: 100%;

  & > thead > tr {
    & > th {
      white-space: nowrap;
    }
  }
}
  .driverTh {
    min-width: calc(40px + 55px);
    width: calc(40px + 55px);
  }
  .nameTh {
  }
  .jobCountTh {
    min-width: 70px;
    width: 70px;
  }
  .typeTh {
    min-width: 70px;
    width: 70px;
  }
  .notesTh {
    min-width: 80px;
  }
  .actionsTh {
    min-width: 40px;
    width: 40px;
  }

  .foldTd {
    min-width: 40px;
    width: 40px;
  }
  .driverTd {
    white-space: nowrap;
  }
    .badge {
      vertical-align: bottom;
      margin-left: -4px;

      :global {
        .v-badge__badge {
          width: 12px;
          height: 12px;
          border-radius: 50%;
        }
      }
    }
  .nameTd {
  }
  .jobCountTd {
  }
  .typeTd {
  }
  .notesTd {
  }
  .actionsTd {
    white-space: nowrap;
    text-align: center;
  }
    .actionsMenuList {
      list-style: none;
      padding: 0;
      margin: -12px -10px;
    }
      .actionsMenuListItem {
        display: inline;
      }
</style>
